import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import {
  Chip,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { listContactLabels, searchContacts } from '../../api/business/endpoints';
import type { ContactKind, ContactSortKey, ContactType } from '../../api/business/types';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatDate } from '../../components/format';
import { ServerPagination } from '../../components/ServerPagination';
import { SortableCell } from '../../components/SortableCell';
import { TableFilters } from '../../components/TableFilters';
import { TableSkeletonRows } from '../../components/TableSkeletonRows';
import { useDebouncedValue } from '../../components/useDebouncedValue';
import { useSort } from '../../components/useSort';
import { useLanguage } from '../../i18n/LanguageContext';

const KINDS: Array<ContactKind> = ['ORGANISATION', 'PERSON'];

const TYPES: Array<ContactType> = ['CUSTOMER', 'SUPPLIER', 'PARTNER', 'OTHER'];

/**
 * A company's contacts (R0039 D-24): server-paginated and sorted, filtered by a search across the card, kind, type,
 * label, country and the do-not-contact flag. A row opens the contact's card.
 */
export const ContactsTable = ({ companyId }: { companyId: string }) => {
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const debouncedTerm = useDebouncedValue(term);
  const [kind, setKind] = useState<ContactKind | ''>('');
  const [type, setType] = useState<ContactType | ''>('');
  const [labelId, setLabelId] = useState('');
  const [country, setCountry] = useState('');
  const [doNotContact, setDoNotContact] = useState<'true' | 'false' | ''>('');
  const [page, setPage] = useState(0);
  const sort = useSort<ContactSortKey>('name', 'asc', () => setPage(0));

  const labels = useQuery({
    queryKey: ['contact-labels', companyId],
    queryFn: () => listContactLabels(companyId),
  });
  const query = useQuery({
    queryKey: [
      'contacts',
      companyId,
      debouncedTerm,
      kind,
      type,
      labelId,
      country,
      doNotContact,
      page,
      sort.key,
      sort.direction,
    ],
    queryFn: () =>
      searchContacts(companyId, {
        search: debouncedTerm.trim() || undefined,
        kind: kind || undefined,
        type: type || undefined,
        labelId: labelId || undefined,
        country: country || undefined,
        doNotContact: doNotContact || undefined,
        page: page + 1,
        sort: sort.key,
        order: sort.direction,
      }),
    placeholderData: keepPreviousData,
  });
  const rows = query.data?.items ?? [];
  const isFiltered =
    debouncedTerm.trim() !== '' ||
    kind !== '' ||
    type !== '' ||
    labelId !== '' ||
    country !== '' ||
    doNotContact !== '';
  const reset = (): void => setPage(0);

  return (
    <Paper variant="outlined">
      <ErrorNotice error={query.error ?? labels.error} onRetry={() => void query.refetch()} />
      <TableFilters>
        <TextField
          size="small"
          label={t('contacts.search')}
          value={term}
          onChange={(event) => {
            setTerm(event.target.value);
            reset();
          }}
          sx={{ minWidth: 300, flexGrow: 1, maxWidth: 420 }}
        />
        <TextField
          select
          size="small"
          label={t('contacts.type')}
          value={type}
          onChange={(event) => {
            setType(event.target.value as ContactType | '');
            reset();
          }}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">{t('common.all')}</MenuItem>
          {TYPES.map((one) => (
            <MenuItem key={one} value={one}>
              {t(`contactType.${one}` as const)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('contacts.kind')}
          value={kind}
          onChange={(event) => {
            setKind(event.target.value as ContactKind | '');
            reset();
          }}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">{t('common.all')}</MenuItem>
          {KINDS.map((one) => (
            <MenuItem key={one} value={one}>
              {t(`contactKind.${one}` as const)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('contacts.label')}
          value={labelId}
          onChange={(event) => {
            setLabelId(event.target.value);
            reset();
          }}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">{t('common.all')}</MenuItem>
          {(labels.data ?? []).map((label) => (
            <MenuItem key={label.id} value={label.id}>
              {label.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('contacts.country')}
          value={country}
          onChange={(event) => {
            setCountry(event.target.value);
            reset();
          }}
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="">{t('common.all')}</MenuItem>
          {(query.data?.countries ?? []).map((one) => (
            <MenuItem key={one} value={one}>
              {one}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('contacts.doNotContact')}
          value={doNotContact}
          onChange={(event) => {
            setDoNotContact(event.target.value as 'true' | 'false' | '');
            reset();
          }}
          sx={{ minWidth: 190 }}
        >
          <MenuItem value="">{t('common.all')}</MenuItem>
          <MenuItem value="true">{t('contacts.dnc.only')}</MenuItem>
          <MenuItem value="false">{t('contacts.dnc.exclude')}</MenuItem>
        </TextField>
      </TableFilters>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortableCell sort={sort} column="name">
                {t('contacts.name')}
              </SortableCell>
              <SortableCell sort={sort} column="type">
                {t('contacts.type')}
              </SortableCell>
              <TableCell>{t('contacts.labels')}</TableCell>
              <SortableCell sort={sort} column="primaryEmail">
                {t('contacts.email')}
              </SortableCell>
              <SortableCell sort={sort} column="primaryPhone">
                {t('contacts.phone')}
              </SortableCell>
              <SortableCell sort={sort} column="city">
                {t('contacts.city')}
              </SortableCell>
              <SortableCell sort={sort} column="country">
                {t('contacts.country')}
              </SortableCell>
              <SortableCell sort={sort} column="ico">
                {t('contacts.ico')}
              </SortableCell>
              <SortableCell sort={sort} column="updatedAt">
                {t('contacts.updatedAt')}
              </SortableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {query.isLoading ? (
              <TableSkeletonRows columns={9} />
            ) : (
              rows.map((contact) => (
                <TableRow
                  key={contact.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/contacts/${companyId}/${contact.id}`)}
                >
                  <TableCell>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 560 }}>
                        {contact.name}
                      </Typography>
                      {contact.doNotContact ? (
                        <Tooltip title={t('contacts.doNotContact')}>
                          <BlockOutlinedIcon fontSize="inherit" color="warning" />
                        </Tooltip>
                      ) : null}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {t(`contactKind.${contact.kind}` as const)}
                    </Typography>
                  </TableCell>
                  <TableCell>{t(`contactType.${contact.type}` as const)}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                      {contact.labels.map((label) => (
                        <Chip key={label.id} size="small" variant="outlined" label={label.name} />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ overflowWrap: 'anywhere', minWidth: 180 }}>
                    {contact.primaryEmail ?? t('common.none')}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {contact.primaryPhone ?? t('common.none')}
                  </TableCell>
                  <TableCell>{contact.city ?? t('common.none')}</TableCell>
                  <TableCell>{contact.country ?? t('common.none')}</TableCell>
                  <TableCell sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>
                    {contact.ico ?? t('common.none')}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatDate(contact.updatedAt, language)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!query.isLoading && rows.length === 0 ? (
        isFiltered ? (
          <EmptyState title={t('contacts.emptyFiltered')} />
        ) : (
          <EmptyState title={t('contacts.empty')} detail={t('contacts.emptyDetail')} />
        )
      ) : null}
      <ServerPagination
        page={page}
        pageSize={query.data?.pageSize ?? 25}
        total={query.data?.total ?? 0}
        onPageChange={setPage}
      />
    </Paper>
  );
};
